import React from "react";
import { useTranslation } from "@coworker/locales";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import InformationCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/InformationCircleIconPath";
import CheckmarkCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CheckmarkCircleIconPath";
import CrossCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CrossCircleIconPath";
import styles from "./TestbuySummaryInfo.module.css";
import { useFormatArrayFieldValue } from "@coworker/apprestructured/src/task-management/hooks/useFormatArrayFieldValue";
import { getStrategyTranslationKey } from "@coworker/apprestructured/src/testbuy/helpers/getStrategyTranslationKey";

type InfoRowProps = {
  icon: (prefix?: string) => React.SVGProps<SVGElement>[];
  text: string;
  iconClassName?: string;
};

type BooleanInfoRowProps = {
  isIncluded: boolean;
  trueText: string;
  falseText: string;
};

type TestbuySummaryInfoProps = {
  articlesCount: number;
  areas: string[];
  strategy: string;
  includeEds: boolean;
  includeOutOfStock: boolean;
};

function InfoRow({ icon, text, iconClassName }: InfoRowProps) {
  const iconElement = <FixaSSRIcon paths={icon} />;
  return (
    <div className={styles["info-row"]}>
      {iconClassName ? (
        <span className={iconClassName}>{iconElement}</span>
      ) : (
        iconElement
      )}
      <FixaText>{text}</FixaText>
    </div>
  );
}

function BooleanInfoRow({
  isIncluded,
  trueText,
  falseText,
}: BooleanInfoRowProps) {
  return (
    <InfoRow
      icon={isIncluded ? CheckmarkCircleIconPath : CrossCircleIconPath}
      text={isIncluded ? trueText : falseText}
      iconClassName={isIncluded ? styles["green"] : styles["red"]}
    />
  );
}

export function TestbuySummaryInfo({
  articlesCount,
  areas,
  strategy,
  includeEds,
  includeOutOfStock,
}: TestbuySummaryInfoProps) {
  // Hooks
  const { t } = useTranslation();
  const formatArrayFieldValue = useFormatArrayFieldValue();

  // Data
  const areasText = formatArrayFieldValue(areas || [], "location-filter");
  const strategyText = getStrategyTranslationKey(strategy);

  return (
    <div className={styles["root"]}>
      <FixaText textStyle="Heading.M" className={styles["title"]}>
        {articlesCount} {t("articlesLowerCaseString")}
        {areas.length ? ` ${t("inString").toLowerCase()} ${areasText}` : ""}
      </FixaText>

      <div className={styles["info-rows"]}>
        <InfoRow icon={InformationCircleIconPath} text={t(strategyText)} />

        {strategy !== "CUSTOM" && (
          <>
            <BooleanInfoRow
              isIncluded={includeEds}
              trueText={t("includeEDSArticlesString")}
              falseText={t("excludeEDSArticlesString")}
            />
            <BooleanInfoRow
              isIncluded={includeOutOfStock}
              trueText={t("includeOutOfStockString")}
              falseText={t("excludeOutOfStockString")}
            />
          </>
        )}
      </div>
    </div>
  );
}
